/* eslint-disable @typescript-eslint/require-await */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { mapCategoryForDisplay } from '@ouster/webviz-react-components';

import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';
import { uniq } from 'lodash';

import {
  DataType,
  DriveValuesGetResponse,
  ExtendedSdkFromOusterFilterableField,
  ExtendedSdkWithOusterFilterableField,
  FilterableField,
  FilterSectionInfo,
  FilterSectionState,
  isFilterableField,
  LoadStatus,
  OrgFilterItem,
  SdkFilterableField,
} from '../types';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from '../utils/constants';
import { fetchWithStatus } from '../utils/state';

/// ///////////////////////////////////////////////////////////////////////////
// Constants
/// ///////////////////////////////////////////////////////////////////////////

const SDK_FIELD_NAMES: Map<SdkFilterableField, string> = new Map([
  [SdkFilterableField.sdk_fw_build_rev, 'Version'],
  [SdkFilterableField.sdk_fw_branch, 'Branch'],
  [SdkFilterableField.sdk_prod_sn, 'Serial Number'],
  [SdkFilterableField.sdk_prod_hw_rev, 'Revision'],
  [SdkFilterableField.sdk_lidar_mode, 'Mode'],
  [SdkFilterableField.sdk_prod_type, 'Type'],
  [SdkFilterableField.sdk_prod_resolution, 'Resolution'],
  [SdkFilterableField.sdk_prod_generation, 'Generation'],
  [SdkFilterableField.sdk_prod_beamspacing, 'Beam Spacing'],
  [SdkFilterableField.uploaded_by, 'Uploader'],
  [SdkFilterableField.data_category, 'Category'],
  [SdkFilterableField.tags, 'Tags'],
]);

const REVERSE_ALPHABETICAL_FIELDS: FilterableField[] = [
  SdkFilterableField.sdk_fw_build_rev,
  SdkFilterableField.sdk_prod_hw_rev,
];

/// ///////////////////////////////////////////////////////////////////////////
// Helper functions
/// ///////////////////////////////////////////////////////////////////////////

export const fieldNameToFilterSection: (
  fieldName: string | FilterableField,
  dataType: DataType
) => FilterSectionInfo = (fieldName, dataType) => {
  if (!isFilterableField(fieldName)) {
    throw new Error(
      `The field ${fieldName} was not filterable. Cannot include it in a filter section info.`
    );
  }

  let field: FilterableField;

  switch (dataType) {
    case DataType.Sdk:
    default:
      field = fieldName as SdkFilterableField;
      return {
        filterField: field,
        name: SDK_FIELD_NAMES.get(field) || '',
      };
  }
};

const driveValuesToFilterState = (
  driveValuesResp: DriveValuesGetResponse,
  dataType: DataType
) => {
  const options = driveValuesResp.map((fieldValues) => {
    const optionsForField: FilterSectionState = {
      sectionInfo: fieldNameToFilterSection(fieldValues.field_name, dataType),
      // filter out null values
      values: fieldValues.values
        .filter((val) => val)
        // Convert numbers to strings
        .map((val) => String(val))
        // Convert to DropdownData
        .map((val) => ({ name: val })),
    };

    // Reverse some values
    if (
      REVERSE_ALPHABETICAL_FIELDS.includes(
        optionsForField.sectionInfo.filterField
      )
    ) {
      optionsForField.values.reverse();
    }
    return optionsForField;
  });

  return options;
};

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

// Most of the state values are optional because they will be first
// undefined, and then initialized based on the URL query parameters in
// the DriveList component
export type State = {
  sdkFilterOptions?: FilterSectionState[];
  orgFilterOptions: OrgFilterItem[];
  sampleFilterOptions: OrgFilterItem[];
  sharedWithOusterFilterOptions: OrgFilterItem[];
  sharedFromOusterFilterOptions: OrgFilterItem[];
  privatePublicFilterOptions: OrgFilterItem[];
  sdkFilterOptionsLoadStatus: LoadStatus;
};

export const defaultState: State = {
  sdkFilterOptionsLoadStatus: 'notStarted',
  orgFilterOptions: [],
  sampleFilterOptions: [],
  sharedWithOusterFilterOptions: [],
  sharedFromOusterFilterOptions: [],
  privatePublicFilterOptions: [],
};

export interface DriveListStateModel extends State {
  loadFilterOptions: EzPzThunk<DriveListStateModel, number>;
  loadOrgFilterOptions: EzPzThunk<DriveListStateModel, number>;
  loadSampleFilterOptions: EzPzThunk<DriveListStateModel>;
  loadSharedWithOusterFilterOptions: EzPzThunk<DriveListStateModel, number>;
  loadSharedFromOusterFilterOptions: EzPzThunk<DriveListStateModel, number>;
  loadPrivatePublicFilterOptions: EzPzThunk<DriveListStateModel, number>;
  setSdkFilterOptionsLoadStatus: EzPzAction<DriveListStateModel, LoadStatus>;
  setSdkFilterOptions: EzPzAction<DriveListStateModel, FilterSectionState[]>;
  setOrgFilterOptions: EzPzAction<DriveListStateModel, OrgFilterItem[]>;
  setSampleFilterOptions: EzPzAction<DriveListStateModel, OrgFilterItem[]>;
  setSharedWithOusterFilterOptions: EzPzAction<
    DriveListStateModel,
    OrgFilterItem[]
  >;
  setSharedFromOusterFilterOptions: EzPzAction<
    DriveListStateModel,
    OrgFilterItem[]
  >;
  setPrivatePublicFilterOptions: EzPzAction<
    DriveListStateModel,
    OrgFilterItem[]
  >;
}

const queryUrl = (dataType: DataType, orgId: number) => {
  let fieldNamesQueryParam = '';
  const fieldNames: FilterableField[] = [...Object.values(SdkFilterableField)];
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });
  return `${BASE_ORG_API}/${orgId}/drive_values/?filter=data_type=${dataType}${fieldNamesQueryParam}`;
};
const sampleQueryUrl = (dataType: DataType) => {
  let fieldNamesQueryParam = '';
  const fieldNames: FilterableField[] = [...Object.values(SdkFilterableField)];
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });
  return `${BASE_PUBLISHED_RECORDING_API}/samples/drive_values?filter=data_type=${dataType}${fieldNamesQueryParam}`;
};

const privatePublicQueryUrl = (dataType: DataType, orgId: number) => {
  let fieldNamesQueryParam = '';
  const fieldNames: FilterableField[] = [...Object.values(SdkFilterableField)];
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });

  return `${BASE_ORG_API}/${orgId}/shared_drives/public_private/drive_values?filter=data_type=${dataType}${fieldNamesQueryParam}`;
};
const shareWithOusterQueryUrl = (dataType: DataType, orgId: number) => {
  let fieldNamesQueryParam = '';
  const fieldNames: string[] = Object.values(
    ExtendedSdkWithOusterFilterableField
  );
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });
  return `${BASE_ORG_API}/${orgId}/shared_drives/drive_values?filter=data_type=${dataType}${fieldNamesQueryParam}`;
};

const shareFromOusterQueryUrl = (dataType: DataType, orgId: number) => {
  let fieldNamesQueryParam = '';
  const fieldNames: string[] = Object.values(
    ExtendedSdkFromOusterFilterableField
  );
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });
  return `${BASE_ORG_API}/${orgId}/shared_drives/org_drive_values?filter=data_type=${dataType}${fieldNamesQueryParam}`;
};

export const model: DriveListStateModel = {
  ...defaultState,
  loadFilterOptions: thunk(async (actions, orgId) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: DriveValuesGetResponse) => {
        actions.setSdkFilterOptions(
          driveValuesToFilterState(sdkDriveValues, DataType.Sdk)
        );
      },
      queryUrl(DataType.Sdk, orgId)
    );
  }),
  loadOrgFilterOptions: thunk(async (actions, orgId) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: OrgFilterItem[]) => {
        sdkDriveValues.map((filterItem) => {
          /*
              For data_category, we will use short mapped list.
              We did that mapping when we loaded the filter options
            */
          if (filterItem.field_name === 'data_category') {
            const mappedCategory = filterItem.values.map((category) =>
              mapCategoryForDisplay(category)
            );

            filterItem.values = uniq(mappedCategory);
            return { ...filterItem };
          }

          return filterItem;
        });

        actions.setOrgFilterOptions(sdkDriveValues);
      },
      queryUrl(DataType.Sdk, orgId)
    );
  }),
  loadSharedWithOusterFilterOptions: thunk(async (actions, orgId) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: OrgFilterItem[]) => {
        actions.setSharedWithOusterFilterOptions(sdkDriveValues);
      },
      shareWithOusterQueryUrl(DataType.Sdk, orgId)
    );
  }),
  loadSharedFromOusterFilterOptions: thunk(async (actions, orgId) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: OrgFilterItem[]) => {
        actions.setSharedFromOusterFilterOptions(sdkDriveValues);
      },
      shareFromOusterQueryUrl(DataType.Sdk, orgId)
    );
  }),
  loadPrivatePublicFilterOptions: thunk(async (actions, orgId) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: OrgFilterItem[]) => {
        actions.setPrivatePublicFilterOptions(sdkDriveValues);
      },
      privatePublicQueryUrl(DataType.Sdk, orgId)
    );
  }),
  loadSampleFilterOptions: thunk(async (actions) => {
    await fetchWithStatus(
      actions.setSdkFilterOptionsLoadStatus,
      (sdkDriveValues: OrgFilterItem[]) => {
        actions.setSampleFilterOptions(sdkDriveValues);
      },
      sampleQueryUrl(DataType.Sdk)
    );
  }),
  setSdkFilterOptionsLoadStatus: action((state, status) => {
    state.sdkFilterOptionsLoadStatus = status;
  }),
  setSdkFilterOptions: action((state, filterOptions) => {
    state.sdkFilterOptions = filterOptions;
  }),
  setOrgFilterOptions: action((state, filterOptions) => {
    state.orgFilterOptions = filterOptions;
  }),
  setSampleFilterOptions: action((state, filterOptions) => {
    state.sampleFilterOptions = filterOptions;
  }),
  setSharedWithOusterFilterOptions: action((state, filterOptions) => {
    state.sharedWithOusterFilterOptions = filterOptions;
  }),
  setSharedFromOusterFilterOptions: action((state, filterOptions) => {
    state.sharedFromOusterFilterOptions = filterOptions;
  }),
  setPrivatePublicFilterOptions: action((state, filterOptions) => {
    state.privatePublicFilterOptions = filterOptions;
  }),
};
